import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import WidgetsView from '../widgets/widgets-view'
import widgetMetadata from '../widgets/widget-metadata'
import getFieldValues from 'scoreboard/datasets/records/get-field-values'
import FormActionType from 'scoreboard/forms/model/form-action-type'
const { SAVE_RECORD, GO_TO_PAGE } = FormActionType
import EvaluateStatementsActor from 'scoreboard/forms/actions/evaluate-statements-actor'
import LoadingDots from 'scoreboard/components/loading-dots'


var FormDirector = Backbone.View.extend({

	/**
	 * Directs the work that makes forms interactive on the Forms Overview.
	 * Keeps track of the current record being added or edited, and asks all
	 * the Actors to perform their actions.
	 *
	 * @constructs
	 * @typedef {Object} FormDirector
	 * @property {DatasetRecord} record the record currently being edited, if
	 * applicable
	 * @property {Object<Number,Object>} fieldValues the values for each of the
	 * fields, keyed by field ID
	 *
	 * @param {Object} options
	 *
	 * @param {Form} options.model
	 *
	 * @param {FormView} options.formView
	 */
	initialize(options = {}) {
		this.options = options
		this.formView = options.formView
		this.treeItemsById = {}
		this.fieldValues = {}
		if (options.model.get('treeItems')) {
			this.treeItemsById = _.keyBy(options.model.get('treeItems'), 'id')
		}

		// #55080
		// Used for scenarios such as preserving tree items between form pages
		Backbone.on('load:dataset-tree-items', treeItems => {
			_.each(treeItems, treeItem => {
				this.treeItemsById[treeItem.id] = treeItem
			})
		})
	},

	getRecord: function() {
		return this.record
	},

	setRecord(record) {
		this.record = record
		if (_.isEmpty(record)) {
			this.fieldValues = {}
		} else {
			this.fieldValues = getFieldValues(this.model.dataset, record)
			_.extend(this.treeItemsById,
				_.keyBy(this.record.treeItems, 'id'))
			if (_.isEmpty(this.record.originalRecord)) {
				this.record.originalRecord = _.cloneDeep(record.record)
			}
		}
	},

	getFieldValue(fieldId) {
		return this.fieldValues[fieldId]
	},

	setFieldValue(fieldId, value) {
		this.fieldValues[fieldId] = value
	},

	getFieldValues() {
		return this.fieldValues
	},

	/**
	 * #46266 the originalRecord we will be sending to the server. If this is the first time saving
	 * the record, there will be no previousRecord, so use the current record's originalRecord in that case
	 * @returns {Any[]}
	 */
	getOriginalRecord() {
		// the last successfully saved record. We don't ever need to care about
		// previousRecord.originalRecord because the save of this record was successful
		const lastSavedRecord = _.get(this.getPreviousRecord(), 'record')
		const thisRecordOriginalRecord = _.get(this.record, 'originalRecord')
		return _.isNil(lastSavedRecord) ? thisRecordOriginalRecord : lastSavedRecord
	},

	getPreviousRecord() {
		if (_.isNil(this.previousRecord)) {
			return []
		}
		return this.previousRecord
	},

	setPreviousRecord(record) {
		this.previousRecord = record
	},

	/** Renders all the widgets in the form */
	render(options={}) {
		if (!this.widgetsView) {
			this.widgetsView = new WidgetsView({
				model: this.model,
				allowInput: true,
				formView: this.formView,
				formDirector: this,
				treeItemsById: this.treeItemsById
			})
		}
		this.$el.html(this.widgetsView.el)

		// #40084 scroll to top of div when rendering a new form
		this.$el.closest('.scroll-container').scrollTop(0)

		this.widgetsView.render()

		// only run the page display actions if this is the initial render, not
		// if it's an action to clear the record and rerender the widgets
		if (options.initialRender) this.runDisplayActions()

		_.invoke(this.formView, 'start')
	},

	runDisplayActions: async function() {
		const actor = new EvaluateStatementsActor({
			formDirector: this,
			model: this.model,
			formView: this.formView,
			statements: this.model.get('statements')
		})
		let statementGroups =
			this.options.widget?.statements ?? this.model.get('statements')

		this.toggleLoadingDots(true)

		// #62630 loop checks statements in isolation, check all actions
		// here instead of inside the loop to handle go-to and save actions
		const { hasGoToPage, hasSaveRecord } =
			this.hasActions(await actor.act())

		// #56953
		// Run through each statement group sequentially to ensure that the
		// data available for the next group has the context of the previous.
		// Run the loop at least once in case the statement group is empty
		// or only contains consequence actions to run.
		let i = 0
		do {
			const statements = statementGroups[i]
			const actions = await actor.act(
				_.size(statementGroups) == 0 ? statementGroups : [statements]
			)
			await this.direct(actions, { hasGoToPage, hasSaveRecord })
			i++
		} while (i < statementGroups.length)

		this.toggleLoadingDots(false)

		this.widgetsView.onDisplayActionsDone()
	},

	toggleLoadingDots: function(show) {
		const el = this.formView.$('.loading-dots-container')
		if (!show) {
			el.addClass('is-not-displayed')
			if (!!this.loadingDots) this.loadingDots.clear()
			return
		}

		el.removeClass('is-not-displayed')
		if (!this.loadingDots) this.loadingDots = new LoadingDots({ el })
		this.loadingDots.render()
	},

	/**
	 * Prepares the appropriate actors to take all the given actions
	 * @param {FormAction[]} actions
	 * @param {Object} [opts]
	 * @param {BusyButton} [opts.busyButton] optional busy button used to display
	 * a loading state until all the actions complete
	 * @param {Boolean} [opts.displayingDoneCheckmark=false]
	 * @param {Boolean} [opts.toggleBusyIfNoActions=false] whether to toggle the
	 * busyButton loading state off if no actions are given
	 * @param {Boolean} [opts.hasGoToPage=false] whether the actions contain a
	 * GO_TO_PAGE action
	 * @param {Boolean} [opts.hasSaveRecord=false] whether the actions contain a
	 * SAVE_RECORD action
	 */
	direct: function (actions, {
		busyButton,
		displayingDoneCheckmark = false,
		toggleBusyIfNoActions = false,
		hasGoToPage = false,
		hasSaveRecord = false
	} = {}) {
		// the form view won't take care of validation for Forms, so we have
		// to add it here any time a save record action is carried out
		if (hasSaveRecord) {
			this.options.formView.clearErrors()
			const validation = this.options.formView.validate()
			if (!_.isEmpty(validation)) return
		}

		actions = sortActionsByGoToPage(actions)

		if (hasGoToPage) this.trigger(GO_TO_PAGE, true)
		if (hasGoToPage || hasSaveRecord) busyButton?.loading()
		// if no actions exists, toggle busyButton loading state off
		if (_.isEmpty(actions) && toggleBusyIfNoActions) busyButton?.enable()


		let promise = this.directActions(
			actions, { busyButton, displayingDoneCheckmark, hasGoToPage })
		promise.then(() => this.trigger('directed'))
		return promise
	},

	/**
	 * Returns a promise that resolves to an object with the hasGoToPage and hasSaveRecord properties
	 * @param {FormAction[]} actions
	 * @returns {Object}
	 */
	hasActions(actions) {
		const hasGoToPage = this.hasAction(actions, GO_TO_PAGE)
		const hasSaveRecord = this.hasAction(actions, SAVE_RECORD)
		return {
			hasGoToPage,
			hasSaveRecord
		}
	}, 

	hasAction(actions, actionType) {
		return _.some(actions, action => action.type === actionType)
	},

	/** Recursively calls all the actions */
	directActions(actions,
		{ busyButton, displayingDoneCheckmark, hasGoToPage }) {
		if (_.isEmpty(actions)) {
			const deferred = $.Deferred()
			deferred.resolve()
			return deferred.promise()
		}
		let action = _.first(actions)
		let message = action.type ? `Executing '${action.type}' action` :
			`Executing callback ${action}`
		console.log(message)
		let deferred
		if (action.type) {
			let actor = this._createActor(action, { busyButton, hasGoToPage })
			displayingDoneCheckmark = displayingDoneCheckmark ||
				actor.isDisplayingDoneCheckmark()
			deferred = $.when(actor.act())
			deferred.then(() => this.trigger('acted', action))
		} else { // assume the action is a callback to execute
			let result = action()
			deferred = $.when(result) // potentially handle a deferred result
		}

		// #52141 if there was a failure to execute the action, re-enable the
		// busy button
		if (displayingDoneCheckmark) {
			deferred.fail(() => busyButton?.enable?.())
		}

		deferred.then(() =>
			this.directActions(_.drop(actions),
				{ busyButton, displayingDoneCheckmark, hasGoToPage }))

		return deferred
	},

	/**
	 * @param {FormAction} action
	 * @return {Actor}
	 */
	_createActor(action, opts={}) {
		let constructor = widgetMetadata.actors[action.type]
		return new constructor(_.extend({}, {
			model: this.model,
			formView: this.formView,
			action,
			formDirector: this,
			guardOptions: this.formView.guardOptions
		}, opts))
	},

	requestParams: function(opts={}) {
		let params = this.requestParamsObj
		return _.isEmpty(params) ? '' : `?${$.param(params)}`
	},

	requestParamsObj: function(opts = {}) {
		let embeddedToken = this.options.embeddedToken
		let params = _.isNil(embeddedToken) ?
			opts : _.extend({}, opts, {embeddedToken})
		return params
	},

	remove() {
		Backbone.View.prototype.render.apply(this, this.arguments)
		this.widgetsView?.remove()
	}

})

/**
 * #39915 #56936
 * Takes a list of actions and sorts them so that GO_TO_PAGE actions are at the
 * end of the list. This is useful for ensuring that all other actions are
 * executed before the page is redirected.
 * @param actions - list of actions to sort
 * @returns {Array} - the sorted list of actions
 */
function sortActionsByGoToPage(actions) {
	return _.sortBy(actions, action => action.type === GO_TO_PAGE)
}

_.extend(FormDirector.prototype, Backbone.Events)

export default FormDirector
export { sortActionsByGoToPage }

FormDirector.__filename__ = 'forms/overview/form-director.js'
